import { CheckCircle2, Trophy, TrendingUp, FolderKanban, Clock, Sparkles, Star } from 'lucide-react';
import { useEffect, useState } from 'react';

function StatBadge({ icon: Icon, label, value, color = 'text-accent' }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-surface-card rounded-lg border border-border">
      <Icon size={16} className={color} />
      <span className="text-sm text-text-secondary">{label}:</span>
      <span className="text-sm font-semibold text-text-primary">{value}</span>
    </div>
  );
}

// Simple celebration particles component
function CelebrationParticles() {
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    delay: `${Math.random() * 2}s`,
    duration: `${2 + Math.random() * 2}s`,
    size: 8 + Math.random() * 8,
    color: ['#C45D3A', '#4A90D9', '#9B6B9E', '#10B981'][Math.floor(Math.random() * 4)]
  }));

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      {particles.map(p => (
        <div
          key={p.id}
          className="absolute animate-fall"
          style={{
            left: p.left,
            animationDelay: p.delay,
            animationDuration: p.duration
          }}
        >
          <Star
            size={p.size}
            fill={p.color}
            color={p.color}
            className="animate-spin"
            style={{ animationDuration: '3s' }}
          />
        </div>
      ))}
      <style>{`
        @keyframes fall {
          0% { transform: translateY(-20px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        .animate-fall {
          animation: fall linear forwards;
        }
      `}</style>
    </div>
  );
}

export default function ReviewComplete({ stats, week, reflection, onDone }) {
  const [showParticles, setShowParticles] = useState(true);

  // Hide particles after animation
  useEffect(() => {
    const timer = setTimeout(() => setShowParticles(false), 4000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-6">
      {/* Celebration animation */}
      {showParticles && <CelebrationParticles />}

      <div className="max-w-md w-full text-center">
        {/* Success icon */}
        <div className="relative mb-6">
          <div className="w-24 h-24 rounded-full bg-success/10 flex items-center justify-center mx-auto">
            <Trophy size={48} className="text-success" />
          </div>
          <div className="absolute -top-2 -right-2 w-10 h-10 rounded-full bg-accent flex items-center justify-center animate-bounce">
            <CheckCircle2 size={24} className="text-white" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-text-primary mb-2">
          Weekly Review Complete!
        </h1>
        <p className="text-text-secondary mb-6">
          {week} is all wrapped up. You're ready for a productive week ahead.
        </p>

        {/* Stats */}
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          <StatBadge
            icon={CheckCircle2}
            label="Tasks Done"
            value={stats.tasksCompleted}
            color="text-success"
          />
          <StatBadge
            icon={FolderKanban}
            label="Projects"
            value={`${stats.projectsReviewed}/${stats.totalProjects}`}
            color="text-weekly"
          />
          <StatBadge
            icon={Clock}
            label="Waiting"
            value={`${stats.waitingForReviewed}/${stats.totalWaitingFor}`}
            color="text-text-tertiary"
          />
          <StatBadge
            icon={TrendingUp}
            label="Habits"
            value={`${stats.habitsRate}%`}
            color="text-monthly"
          />
          {stats.inboxProcessed > 0 && (
            <StatBadge
              icon={Sparkles}
              label="Inbox Cleared"
              value={stats.inboxProcessed}
              color="text-accent"
            />
          )}
        </div>

        {/* Reflection summary */}
        {reflection && (reflection.wentWell || reflection.nextWeekFocus) && (
          <div className="bg-surface-card rounded-xl border border-border p-4 mb-6 text-left">
            {reflection.wentWell && (
              <div className="mb-3">
                <p className="text-xs font-medium text-success mb-1">What went well</p>
                <p className="text-sm text-text-secondary">{reflection.wentWell}</p>
              </div>
            )}
            {reflection.nextWeekFocus && (
              <div>
                <p className="text-xs font-medium text-accent mb-1">Next week focus</p>
                <p className="text-sm text-text-secondary">{reflection.nextWeekFocus}</p>
              </div>
            )}
          </div>
        )}

        {/* Done button */}
        <button
          onClick={onDone}
          className="w-full py-3 px-6 bg-accent text-white rounded-xl font-medium
                   hover:bg-orange-hover transition-colors border-none cursor-pointer"
        >
          Done
        </button>

        {/* Motivational quote */}
        <p className="mt-6 text-sm text-text-tertiary italic">
          "Your mind is for having ideas, not holding them." — David Allen
        </p>
      </div>
    </div>
  );
}
